import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import ForwardToInboxOutlined from "@mui/icons-material/ForwardToInboxOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import type { ReactElement } from "react";
import {
  Logout,
  MenuItemLink,
  useCanAccessResources,
  UserMenu as RaUserMenu,
  UserMenuProps,
  useTranslate,
  useUserMenu,
} from "react-admin";

import type { PortalResource } from "../auth/access";
import {
  MARKETING_DELIVERY_ROUTE,
  ResourceName,
  SALESFORCE_INTEGRATION_ROUTE,
  TEAM_ROUTE,
} from "../constants/resources";

type UserMenuItemProps = {
  icon: ReactElement;
  label: string;
  testId?: string;
  to: string;
};

const UserMenuItem = ({ icon, label, testId, to }: UserMenuItemProps) => {
  const userMenu = useUserMenu();

  return <MenuItemLink to={to} primaryText={label} leftIcon={icon} onClick={userMenu?.onClose} data-testid={testId} />;
};

const USER_MENU_RESOURCES: PortalResource[] = [
  ResourceName.Location,
  ResourceName.SalesforceIntegration,
  ResourceName.MarketingDelivery,
  ResourceName.Staff,
];

export const CustomUserMenu = (props: UserMenuProps) => {
  const translate = useTranslate();
  const { canAccess } = useCanAccessResources({ action: "list", resources: USER_MENU_RESOURCES });

  // Integration settings belong to the manager's organization.
  const hasManagerAccess = canAccess?.[ResourceName.Location] === true;

  return (
    <RaUserMenu {...props}>
      <UserMenuItem
        to="/account"
        label={translate("resources.account")}
        icon={<AccountCircleOutlinedIcon />}
        testId="account-settings-menu-item"
      />
      {hasManagerAccess && (
        <UserMenuItem
          to="/integration-settings"
          label={translate("resources.integration-settings")}
          icon={<SettingsIcon />}
        />
      )}
      {canAccess?.[ResourceName.Staff] === true && (
        <UserMenuItem
          to={TEAM_ROUTE}
          label={translate("resources.team")}
          icon={<GroupsOutlined />}
          testId="team-menu-item"
        />
      )}
      {canAccess?.[ResourceName.SalesforceIntegration] === true && (
        <UserMenuItem
          to={SALESFORCE_INTEGRATION_ROUTE}
          label={translate("resources.salesforce-integration")}
          icon={<SettingsIcon />}
          testId="salesforce-integration-menu-item"
        />
      )}
      {canAccess?.[ResourceName.MarketingDelivery] === true && (
        <UserMenuItem
          to={MARKETING_DELIVERY_ROUTE}
          label={translate("resources.marketing-delivery")}
          icon={<ForwardToInboxOutlined />}
          testId="marketing-delivery-menu-item"
        />
      )}
      <Logout />
    </RaUserMenu>
  );
};
